<template>
  <div>
    <popup-hint hint="nonexcises">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Non-excise alcohol">
          <template v-slot:left v-if="lowInventoryCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{lowInventoryCount}} non-excise alcohol{{lowInventoryCount>1?'s':''}} with low inventory
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.siteId=null;filters.status='1';filters.search='';filters.lowInventory='1';filters.asof=today})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Non-excise alcohol can be used for non-excise reportable spirits like heads, denatured alcohol, and bulk hand sanitizer.
        Like spirits or wine, non-excise alcohol can be kept in tanks or bulk containers.
        It is <em>not</em> tracked for excise purposes.
      </template>
      <template v-slot:bottom>
          You can create non-excise alcohol individually or do a batch import.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'AlcoholNonExcisesEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <import-csv v-model="doImport" type="non-excises" :fields="importFields" importAction="nonExcisesImport" :instructions="instructions" ref="import" @refresh="$refs.table.buildList()"/>

    <purtrak-table name="nonExcises" objectType="non-excise alcohols" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select
                   listEndPoint="nonExcises2" v-model="selected"
                   fields="id,name,active,sku,preferredVendor{id},reorderPoint,onOrder,onSalesOrder,lowInventory,
                            volume,value,
                            tankVolume,tankValue,
                            bulkContainerVolume,bulkContainerValue"
                   totalFields="totalVolume,totalValue,totalTankVolume,totalTankValue,totalBulkContainerVolume,totalBulkContainerValue"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="nonExcise => $router.push({name:'AlcoholNonExcisesTransactions',params:{nonExciseId:nonExcise.id},query:{siteId:filters.siteId}})"
                   :item-class="nonExcise => (nonExcise.active ? '' : 'grey--text')"
                   title="Non-excise alcohols"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,
                                          filters.lowInventory=='1'?'Low inventory only':'',
                                          filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                          filters.siteId!=null?siteName:'']}"
                   download-endpoint="nonExcisesExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.siteId=null;filters.status='1';filters.lowInventory='0';filters.asof=today;filters.search=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'SKU',value:'sku'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.lowInventory" true-value="1" false-value="0" label="Low inventory only" class="mt-n4"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.lowInventory=='1'" close small class="ml-1" @click:close="filters.lowInventory='0'">Low inventory only</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'AlcoholNonExcisesEditNew'}"><v-list-item-title>Create non-excise</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Non-excises',to:{name:'AlcoholNonExcises'}}]},query:{to:'reports/inventory/ReportsNonExciseActivity'}}"><v-list-item-title>Non-excise activity report</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Non-excises',to:{name:'AlcoholNonExcises'}}]},query:{to:'reports/inventory/ReportsNonExcisesPeriodSummary'}}"><v-list-item-title>Non-excise summary report</v-list-item-title></v-list-item>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-divider/>
          <v-list-item @click="doImport=true"><v-list-item-title>Import non-excises from CSV</v-list-item-title></v-list-item>
        </template>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync non-excise with QBO</v-list-item-title></v-list-item>
        </template>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <alcohol-non-excise-batch-edit :nonExcises="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'AlcoholNonExcisesEdit',params:{nonExciseId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.tankVolume="{item}">
        {{item.tankVolume | number(3)}}
      </template>
      <template v-slot:item.bulkContainerVolume="{item}">
        {{item.bulkContainerVolume | number(3)}}
      </template>
      <template v-slot:item.volume="{item,printing}">
        <template v-if="printing">
          {{item.volume | number(3)}}
        </template>
        <span v-else :class="{'warning--text':item.lowInventory}">{{item.volume | number(3)}}</span>
        <div v-if="!printing && item.lowInventory" class="text-caption">
          <v-btn rounded color="warning lighten-1" x-small :to="{name:'PurchasingPurchaseOrdersEditNew',params:{itemType:'NonExcise',itemId:item.id}}">Create PO</v-btn>
        </div>
      </template>
      <template v-slot:item.tankValue="{item}">
        {{item.tankValue | number(2)}}
      </template>
      <template v-slot:item.bulkContainerValue="{item}">
        {{item.bulkContainerValue | number(2)}}
      </template>
      <template v-slot:item.value="{item,printing}">
        {{item.value | number(2)}}
      </template>
      <template v-slot:item.onOrder="{item}">
        <template v-if="item.onOrder">
          {{item.onOrder | number(3)}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:item.onSalesOrder="{item}">
        <template v-if="item.onSalesOrder">
          {{item.onSalesOrder | number(3)}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'AlcoholNonExcisesEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Non-excise operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'AlcoholNonExcisesTransactions',params:{nonExciseId:item.id},query:{siteId:filters.siteId}}"><v-list-item-title>Transactions</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template>
              <v-divider/>
              <v-list-item :to="{name:'PurchasingPurchaseOrdersEditNew',params:{vendorId:item.preferredVendor?item.preferredVendor.id:'',itemType:'NonExcise',itemId:item.id}}"><v-list-item-title>Create purchase order</v-list-item-title></v-list-item>
              <v-list-item :to="{name:'PurchasingInventoryReceiptsEditNew',params:{vendorId:item.preferredVendor?item.preferredVendor.id:'',itemType:'NonExcise',itemId:item.id}}"><v-list-item-title>Create inventory receipt</v-list-item-title></v-list-item>
            </template>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'AlcoholNonExcisesEdit',params:{nonExciseId:item.id}}"><v-list-item-title>Edit non-excise</v-list-item-title></v-list-item>
              <v-list-item @click="nonExcise=item;showDelete=true"><v-list-item-title>Delete non-excise</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='tankVolume'">
              {{totals.totalTankVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='bulkContainerVolume'">
              {{totals.totalBulkContainerVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='volume'">
              {{totals.totalVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='tankValue'">
              {{totals.totalTankValue | number(2)}}
            </td>
            <td class="text-right" v-else-if="header.value==='bulkContainerValue'">
              {{totals.totalBulkContainerValue | number(2)}}
            </td>
            <td class="text-right" v-else-if="header.value==='value'">
              {{totals.totalValue | number(2)}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="nonExcise.name" @delete="performDeleteNonExcise"/>
  </div>
</template>

<script>
import AlcoholNonExciseBatchEdit from '@/views/alcohol/nonExcise/AlcoholNonExciseBatchEdit'
import {format} from 'date-fns'

export default {
  name: 'AlcoholNonExcises',
  components: {AlcoholNonExciseBatchEdit},
  data() {
    return {
      filters: {
        lowInventory: '0',
        asof: format(new Date(), 'yyyy-MM-dd'),
        status: '1',
        userLicense: '0',
        siteId: null,
        search: '',
        searchField: 'name'
      },
      lowInventoryCount: 0,
      today: format(new Date(), 'yyyy-MM-dd'),
      showStatus: false,
      searchDisplay: '',
      nonExcise: {},
      totals: {totalVolume:0, totalValue:0,
               totalTankVolume:0, totalTankValue: 0,
               totalBulkContainerVolume:0, totalBulkContainerValue: 0},
      showDelete: false,
      doImport: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true},{name:'SKU',mapTo:'',mandatory:false}],
      instructions: `<ol class="mb-4">
                        <li>Name</li>
                        <li>SKU</li>
                      </ol>
                      <em>Note that not all information for a non-excise can be imported.</em>
                      You should, therefore, check each imported non-excise to ensure all settings are correct.`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'sku', visible:false},
                       { value: 'onOrder', visible:false},
                       { value: 'onSalesOrder', visible:false},
                       { value: 'tankVolume', visible:true},
                       { value: 'bulkContainerVolume', visible:true},
                       { value: 'volume', visible:true, excludeFromExport:true},
                       { value: 'tankValue', visible:false},
                       { value: 'bulkContainerValue', visible:false},
                       { value: 'value', visible:false, excludeFromExport:true}],
      headers: [{ text: 'NAME', value: 'name', width:'35%',sortable:true},
                { text: 'SKU', value: 'sku', width:'10%',sortable:true},
                { text: 'ON PO', value: 'onOrder', width:'15%',sortable:false,align:'right'},
                { text: 'ON SO', value: 'onSalesOrder', width:'15%',sortable:false,align:'right'},
                { text: 'TANKS LITRES', value: 'tankVolume', width:'15%',align:'right',sortable:true},
                { text: 'CONTAINERS LITRES', value: 'bulkContainerVolume', width:'15%',sortable:true,align:'right'},
                { text: 'TOTAL LITRES', value: 'volume', width:'15%',sortable:true,align:'right'},
                { text: 'TANKS $', value: 'tankValue', width:'13%',align:'right',sortable:true},
                { text: 'CONTAINERS $', value: 'bulkContainerValue', width:'13%',sortable:true,align:'right'},
                { text: 'TOTAL $', value: 'value', width:'16%',sortable:true,align:'right'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write') &&
             !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.lowInventory == '1')
        filters.push({field:'lowInventory', comparator:'eq', value:'1'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});

      return filters;
    },
    listParameters() {
      const parameters = [];
      if (this.filters.asof)
        parameters.push({name:'asof', value:`${this.filters.asof} 23:59:59`});
      if (this.filters.siteId)
        parameters.push({name:'siteId', value:this.filters.siteId});
      return parameters;
    }
  },
  async created() {
    const results = await this.$apiCall(`query AlcoholNonExcises {
                                            nonExcisesLowInventory
                                          }`,
                                      {});
    if (!results.error)
      this.lowInventoryCount = results.data.nonExcisesLowInventory;

    if (!['NonExciseEdit', 'NonExciseEditNew', 'NonExciseTransactions'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.lowInventoryCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    async performDeleteNonExcise() {
      this.loading = true;
      const results = await this.$apiCall(`mutation AlcoholNonExcises($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.nonExcise.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table?.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted non-excise ${this.nonExcise.name}`});
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation AlcoholNonExcises {
                                             accountingIntegrationSync(step:"nonExcises") {name}
                                           }`, {});
      this.$refs.table?.buildList();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced non-excise alcohols with QBO`});
    }
  }
}
</script>